import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PhoneFrame from './components/PhoneFrame';
import MobileAppContent from './components/MobileAppContent';
import PreviewControls from './components/PreviewControls';
import ContentValidation from './components/ContentValidation';

const MobileAppPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentSection, setCurrentSection] = useState('home');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userRole, setUserRole] = useState('artist');

  // Mock artist data - in real app this would come from props or API
  const [artistData] = useState({
    profile: {
      name: "Elena Rodriguez",
      email: "elena@artist.com",
      phone: "+1 (555) 123-4567",
      website: "www.elenarodriguez.com",
      artistType: "Visual",
      socialLinks: {
        instagram: "@elenarodriguez",
        twitter: "@elena_art",
        linkedin: "elena-rodriguez-artist"
      }
    },
    promotion: {
      splashImage: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400",
      homeImage: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400",
      logo: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=100",
      featuredItem: "Art Exhibition Opening"
    },
    about: {
      headerImage: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400",
      description: `Elena Rodriguez is a contemporary visual artist known for her vibrant abstract paintings and mixed-media installations.\n\nWith over 15 years of experience, she has exhibited in galleries across North America and Europe. Her work explores themes of identity, culture, and human connection through bold colors and dynamic compositions.\n\nElena's art has been featured in numerous publications and she continues to push boundaries in the contemporary art world.`,
      bookingContact: "booking@elenarodriguez.com"
    },
    calendar: [
      {
        id: 1,
        title: "Solo Exhibition Opening",
        date: "2025-01-15",
        time: "6:00 PM",
        venue: "Modern Art Gallery",
        description: "Opening night for Elena's latest collection 'Urban Reflections'",
        image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300",
        url: "https://modernartgallery.com/elena-rodriguez"
      },
      {
        id: 2,
        title: "Artist Talk & Workshop",
        date: "2025-01-22",
        time: "2:00 PM",
        venue: "Community Arts Center",
        description: "Interactive workshop on abstract painting techniques",
        image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=300",
        url: "https://communityarts.org/workshops"
      },
      {
        id: 3,
        title: "Art Fair Participation",
        date: "2025-02-05",
        time: "10:00 AM",
        venue: "Convention Center",
        description: "Showcasing latest works at the annual contemporary art fair",
        image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=300"
      }
    ],
    work: [
      {
        id: 1,
        title: "Urban Symphony #1",
        mediaType: "Acrylic on Canvas",
        dimensions: "48\" x 36\"",
        price: 2500,
        collectible: true,
        printSales: true,
        shopIncluded: true,
        availability: "available",
        creationDate: "2024-11-15",
        image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400"
      },
      {
        id: 2,
        title: "Reflections in Blue",
        mediaType: "Mixed Media",
        dimensions: "60\" x 40\"",
        price: 3200,
        collectible: true,
        printSales: false,
        shopIncluded: false,
        availability: "sold",
        creationDate: "2024-10-22",
        image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400"
      },
      {
        id: 3,
        title: "Abstract Emotions",
        mediaType: "Oil on Canvas",
        dimensions: "36\" x 24\"",
        price: 1800,
        collectible: false,
        printSales: true,
        shopIncluded: true,
        availability: "available",
        creationDate: "2024-12-01",
        image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400"
      }
    ],
    shop: {
      productTypes: ["T-shirts", "Posters", "Prints", "Tote Bags"],
      featured: [
        {
          id: 1,
          name: "Urban Symphony Print",
          price: 45,
          type: "Print",
          image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300"
        },
        {
          id: 2,
          name: "Artist Collection T-Shirt",
          price: 28,
          type: "T-shirt",
          image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300"
        }
      ]
    }
  });

  // Determine user role based on route or localStorage
  useEffect(() => {
    const savedRole = localStorage.getItem('userRole');
    if (savedRole) {
      setUserRole(savedRole);
    } else {
      // Determine role based on current route
      const currentPath = location?.pathname;
      if (currentPath?.includes('admin')) {
        setUserRole('admin');
      } else if (currentPath?.includes('editor')) {
        setUserRole('editor');
      } else {
        setUserRole('artist');
      }
    }
  }, [location]);

  const handleSectionChange = (section) => {
    setCurrentSection(section);
  };

  const handleRefresh = () => {
    // Simulate refresh with loading state
    setCurrentSection('home');
    setTimeout(() => {
      setCurrentSection(currentSection);
    }, 500);
  };

  const handleClose = () => {
    // Navigate back based on user role
    switch (userRole) {
      case 'admin': navigate('/admin-dashboard');
        break;
      case 'editor': navigate('/editor-dashboard');
        break;
      case 'artist': navigate('/artist-content-management');
        break;
      default:
        navigate('/login');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleBackNavigation = () => {
    switch (userRole) {
      case 'admin': navigate('/admin-dashboard');
        break;
      case 'editor': navigate('/artist-detail-editor-view');
        break;
      case 'artist': navigate('/artist-content-management');
        break;
      default:
        navigate('/login');
    }
  };

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex items-center justify-center">
        <div className="absolute top-4 right-4 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white hover:bg-opacity-20"
          >
            <Icon name="Minimize2" size={20} />
          </Button>
        </div>
        <PhoneFrame artistData={artistData} currentSection={currentSection}>
          <MobileAppContent artistData={artistData} currentSection={currentSection} />
        </PhoneFrame>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole={userRole} />
      <Sidebar 
        userRole={userRole} 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`pt-16 transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackNavigation}
                iconName="ArrowLeft"
              />
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Mobile App Preview</h1>
                <p className="text-text-secondary">
                  Preview how {artistData?.profile?.name}'s content appears in the mobile app
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={toggleFullscreen}
                iconName="Maximize2"
                iconPosition="left"
                iconSize={16}
              >
                Fullscreen
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={16}
              >
                Refresh
              </Button>
              <Button
                variant="default"
                onClick={handleClose}
                iconName="X"
                iconPosition="left"
                iconSize={16}
              >
                Close Preview
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Mobile Preview - Takes up 2 columns on xl screens */}
            <div className="xl:col-span-2 flex justify-center">
              <div className="relative">
                <PhoneFrame artistData={artistData} currentSection={currentSection}>
                  <MobileAppContent artistData={artistData} currentSection={currentSection} />
                </PhoneFrame>
                
                {/* Section Indicator */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                  <div className="bg-white rounded-full px-4 py-2 shadow-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {currentSection} Screen
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls and Validation - Each takes 1 column */}
            <div className="space-y-6">
              <PreviewControls
                currentSection={currentSection}
                onSectionChange={handleSectionChange}
                onRefresh={handleRefresh}
                onClose={handleClose}
                artistData={artistData}
              />
            </div>

            <div className="space-y-6">
              <ContentValidation artistData={artistData} />
            </div>
          </div>

          {/* Quick Navigation Bar */}
          <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-center space-x-2">
              {['home', 'about', 'calendar', 'work', 'shop']?.map((section) => (
                <Button
                  key={section}
                  variant={currentSection === section ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleSectionChange(section)}
                  className="capitalize"
                >
                  {section}
                </Button>
              ))}
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center text-sm text-text-secondary">
            <p>
              Preview last updated: {new Date()?.toLocaleString()} • 
              Device: iPhone 13 Pro (375×812) • 
              Version: 2.1.0
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MobileAppPreview;